import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Rating,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
} from '@mui/icons-material';
import { useSearch } from '@/contexts/SearchContext';
import { PublicListing } from '@/contexts/SearchContext';
import ImageCarousel from './ImageCarousel';

interface PropertyCardProps {
  listing: PublicListing;
  onCardClick?: (listing: PublicListing) => void;
  onFavoriteClick?: (listingId: string, isFavorite: boolean) => void;
  showInstantBook?: boolean;
  loading?: boolean;
  variant?: 'default' | 'compact';
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  listing,
  onCardClick,
  onFavoriteClick,
  showInstantBook = true,
  loading = false,
  variant = 'default',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isFavorite, toggleFavorite } = useSearch();
  
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isListingFavorite = isFavorite(listing.listingId);

  // Handle favorite toggle
  const handleFavoriteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    toggleFavorite(listing.listingId);
    onFavoriteClick?.(listing.listingId, !isListingFavorite);
  }, [listing.listingId, isListingFavorite, toggleFavorite, onFavoriteClick]);

  // Handle card click
  const handleCardClick = useCallback(() => {
    onCardClick?.(listing);
  }, [listing, onCardClick]);

  // Handle image carousel click
  const handleImageClick = useCallback((imageIndex: number) => {
    // For now, just trigger the card click
    // In the future, this could open a full-screen image gallery
    handleCardClick();
  }, [handleCardClick]);

  // Format location display
  const getLocationText = () => {
    const { address } = listing;
    if (address.neighborhood && address.city) {
      return `${address.neighborhood}, ${address.city}`;
    }
    if (address.city && address.state) {
      return `${address.city}, ${address.state}`;
    }
    return address.city || address.state || address.country;
  };

  // Format price display
  const getPriceText = () => {
    const { basePrice, currency } = listing.pricing;
    const currencySymbol = currency === 'USD' ? '$' : currency;
    return `${currencySymbol}${basePrice}`;
  };

  // Get property features text
  const getPropertyFeatures = () => {
    const features = [];
    if (listing.maxGuests) features.push(`${listing.maxGuests} guests`);
    if (listing.bedrooms) features.push(`${listing.bedrooms} bed${listing.bedrooms > 1 ? 's' : ''}`);
    if (listing.bathrooms) features.push(`${listing.bathrooms} bath${listing.bathrooms > 1 ? 's' : ''}`);
    return features.join(' • ');
  };

  // Loading skeleton
  if (loading) {
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: 'none',
          border: 1,
          borderColor: 'divider',
          height: variant === 'compact' ? 280 : 320,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Skeleton
            variant="rectangular"
            height={variant === 'compact' ? 160 : 200}
            sx={{ borderRadius: '12px 12px 0 0' }}
          />
        </Box>
        <CardContent sx={{ p: 2 }}>
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="60%" height={20} sx={{ mt: 0.5 }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Skeleton variant="text" width="30%" height={24} />
            <Skeleton variant="text" width="25%" height={20} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const cardHeight = variant === 'compact' ? 280 : 320;
  const imageHeight = variant === 'compact' ? 160 : 200;

  return (
    <Card
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderRadius: 3,
        boxShadow: 'none',
        border: 1,
        borderColor: 'divider',
        cursor: onCardClick ? 'pointer' : 'default',
        height: cardHeight,
        transition: 'all 0.2s ease-in-out',
        '&:hover': onCardClick ? {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
          borderColor: 'transparent',
        } : {},
        '&:focus-within': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Image Section */}
      <Box sx={{ position: 'relative', height: imageHeight }}>
        <ImageCarousel
          images={listing.photos.length > 0 ? listing.photos : ['/placeholder-property.jpg']}
          alt={listing.title}
          aspectRatio={1.25} // Slightly wider than square for property cards
          showDots={listing.photos.length > 1}
          showArrows={listing.photos.length > 1}
          onImageClick={handleImageClick}
          borderRadius={12}
          loading={false}
        />

        {/* Favorite Button Overlay */}
        <IconButton
          onClick={handleFavoriteClick}
          sx={{
            position: 'absolute',
            top: isMobile ? 8 : 12,
            right: isMobile ? 8 : 12,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: isListingFavorite ? 'error.main' : 'text.secondary',
            width: isMobile ? 40 : 32,
            height: isMobile ? 40 : 32,
            zIndex: 3,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              color: isListingFavorite ? 'error.dark' : 'error.main',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
          aria-label={isListingFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isListingFavorite ? (
            <FavoriteIcon fontSize="small" />
          ) : (
            <FavoriteBorderIcon fontSize="small" />
          )}
        </IconButton>

        {/* Instant Book Badge */}
        {showInstantBook && listing.instantBook && (
          <Chip
            label="Instant Book"
            size={isMobile ? 'medium' : 'small'}
            sx={{
              position: 'absolute',
              top: isMobile ? 8 : 12,
              left: isMobile ? 8 : 12,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600,
              fontSize: isMobile ? '0.8rem' : '0.75rem',
              zIndex: 3,
            }}
          />
        )}
      </Box>

      {/* Content Section */}
      <CardContent sx={{ 
        p: isMobile ? 2.5 : 2, 
        height: cardHeight - imageHeight, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        {/* Location and Rating */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <LocationIcon fontSize="small" />
              {getLocationText()}
            </Typography>
          </Box>
          
          {/* Placeholder for star rating */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Rating
              value={4.5} // Placeholder rating
              precision={0.1}
              size="small"
              readOnly
              sx={{
                '& .MuiRating-icon': {
                  fontSize: '1rem',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              (4.5)
            </Typography>
          </Box>
        </Box>

        {/* Property Title */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
            fontSize: isMobile ? '1.1rem' : '1rem',
          }}
          title={listing.title}
        >
          {listing.title}
        </Typography>

        {/* Property Features */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getPropertyFeatures()}
        </Typography>

        {/* Property Type */}
        {listing.propertyType && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mb: 1,
              textTransform: 'capitalize',
            }}
          >
            {listing.propertyType}
          </Typography>
        )}

        {/* Price and Per Night */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                display: 'inline',
                fontSize: isMobile ? '1.2rem' : '1.1rem',
              }}
            >
              {getPriceText()}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'inline',
                ml: 0.5,
              }}
            >
              / night
            </Typography>
          </Box>

          {/* Additional pricing info */}
          {listing.pricing.cleaningFee && (
            <Typography variant="caption" color="text.secondary">
              + ${listing.pricing.cleaningFee} cleaning
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;